import { useEffect, useMemo, useState } from "react";

/**
 * Client-side search + pagination over an already-fetched array.
 *
 * The dashboard-bi-v2 ranking/advance endpoints return fixed top-N / full
 * lists with no server pagination, so filtering and slicing happen here.
 *
 * - filters `rows` by case-insensitive substring match on `searchSelector`;
 * - resets `page` to 1 whenever the search term changes;
 * - slices the filtered rows down to the current page.
 */
export interface UseClientTableOptions<T> {
  searchSelector: (row: T) => string;
  pageSize: number;
}

export interface UseClientTableResult<T> {
  pageRows: T[];
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  filteredCount: number;
}

export const useClientTable = <T>(
  rows: T[],
  { searchSelector, pageSize }: UseClientTableOptions<T>,
): UseClientTableResult<T> => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => searchSelector(row).toLowerCase().includes(term));
  }, [rows, search, searchSelector]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  // Reset to the first page whenever the search term changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Keep `page` within bounds if the filtered set shrinks.
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  return {
    pageRows,
    search,
    setSearch,
    page,
    setPage,
    totalPages,
    filteredCount: filteredRows.length,
  };
};
